import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Home, Key, MapPin, Mail, Phone, ExternalLink, FileText, ArrowRight, ShieldCheck, Calculator, Tag, Sparkles, CheckCircle2, Search, User, Globe, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ContactForm } from "@/components/ContactForm";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { PropertySearchFilter } from "@/components/PropertySearchFilter";
import { useListings } from "@/hooks/use-listings";
import { PROPERTY_CATEGORY_LABELS } from "@/lib/listing-quality";
import { BLOG_POSTS } from "@/data/blogs";
import { HeroCarousel } from "@/components/HeroCarousel";
import gurkanImage from "@/assets/gurkan-sonmez.jpg";
import "./HomeMotion.css";

const Index = () => {
  const homeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = homeRef.current;
    if (!root || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const sections = root.querySelectorAll('section:not(:first-of-type)');
    sections.forEach((section) => section.classList.add('corner-reveal'));
    root.classList.add('motion-ready');
    const observer = new IntersectionObserver((entries) => { entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); } }); }, { threshold: 0.05 });
    sections.forEach((section) => observer.observe(section));
    return () => { observer.disconnect(); root.classList.remove('motion-ready'); };
  }, []);
  const navigate = useNavigate();
  const { listings, loading, error } = useListings();
  const [activeTab, setActiveTab] = useState<"satilik" | "kiralik">("satilik");
  const [leadForm, setLeadForm] = useState({ name: "", emailPhone: "", agreed: false });

  const activeCatalog = listings;
  const saleListings = activeCatalog.filter((p) => p.type === "satilik" && p.status !== "archived").slice(0, 6);
  const rentListings = activeCatalog.filter((p) => p.type === "kiralik" && p.status !== "archived").slice(0, 6);
  const displayedListings = activeTab === "satilik" ? saleListings : rentListings;
  const recentNews = BLOG_POSTS.slice(0, 4);

  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.agreed) {
      alert("Lütfen kullanım şartlarını kabul ediniz.");
      return;
    }
    const message = `Merhaba Corner Gayrimenkul, ben ${leadForm.name}. İletişim bilgilerim: ${leadForm.emailPhone}. Ücretsiz danışmanlık talep ediyorum.`;
    window.open(`https://wa.me/905335487801?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <>
      <SEOHead
        title="Corner Gayrimenkul | Adana Emlak ve Gayrimenkul Satış Şirketi"
        description="Corner Gayrimenkul ile Adana'nın 15 ilçesinde satılık ve kiralık gayrimenkul ilanlarını, bölge rehberlerini ve danışmanlık hizmetlerini inceleyin."
        canonicalPath="/"
      />
      <div ref={homeRef} className="corner-home min-h-screen bg-background flex flex-col justify-between font-sans">
        <HeaderNavigation />

        {/* TEKCE Style Hero Banner & Search Section */}
        <section className="relative min-h-[80vh] flex flex-col justify-between overflow-hidden bg-slate-950 py-12 px-4 sm:px-6 lg:px-8">
          <HeroCarousel />
          
          <div className="relative z-10 max-w-7xl mx-auto text-center w-full my-auto py-8">
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000 max-w-4xl mx-auto space-y-6">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/30 backdrop-blur-md text-amber-300 text-xs font-black uppercase tracking-widest border border-amber-400/40 shadow-lg">
                ADANA GENELİNDE GAYRİMENKUL DANIŞMANLIĞI
              </span>
              
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight drop-shadow-md leading-tight">
                Hayalinizdeki Evi Bulun
              </h1>

              {/* Quick Location Pills */}
              <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                <Link to="/adana/seyhan/gurselpasa" className="px-4 py-2 rounded-xl bg-white/10 hover:bg-primary text-white text-xs font-extrabold backdrop-blur-md transition-all border border-white/20">
                  GÜRSELPAŞA
                </Link>
                <Link to="/adana/cukurova/guzelyali" className="px-4 py-2 rounded-xl bg-white/10 hover:bg-primary text-white text-xs font-extrabold backdrop-blur-md transition-all border border-white/20">
                  ÇUKUROVA
                </Link>
                <Link to="/adana/seyhan/pinar" className="px-4 py-2 rounded-xl bg-white/10 hover:bg-primary text-white text-xs font-extrabold backdrop-blur-md transition-all border border-white/20">
                  SEYHAN
                </Link>
                <Link to="/arsa" className="px-4 py-2 rounded-xl bg-white/10 hover:bg-primary text-white text-xs font-extrabold backdrop-blur-md transition-all border border-white/20">
                  KABASAKAL ARSA
                </Link>
                <Link to="/luks-projeler" className="px-4 py-2 rounded-xl bg-amber-500/80 hover:bg-amber-600 text-slate-950 text-xs font-black backdrop-blur-md transition-all">
                  ⭐ LÜKS PROJELER
                </Link>
              </div>

              {/* Tekce-style Floating Property Search Filter */}
              <div className="mt-8 text-left bg-slate-900/90 backdrop-blur-xl p-4 sm:p-6 rounded-3xl border border-slate-800 shadow-2xl">
                <PropertySearchFilter />
              </div>
            </div>
          </div>
        </section>

        {/* TEKCE Quick Guides / Trust Cards Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Link to="/hakkimizda" className="bg-card p-5 rounded-2xl border border-border/80 shadow-sm hover:shadow-xl hover:border-primary transition-all group flex flex-col justify-between space-y-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-black text-sm text-foreground group-hover:text-primary transition-colors">Adana'nın 15 İlçesi</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">İlçe rehberleriyle bölgenizi tanıyın, mevcut portföyü inceleyin.</p>
              </div>
            </Link>

            <Link to="/hakkimizda" className="bg-card p-5 rounded-2xl border border-border/80 shadow-sm hover:shadow-xl hover:border-primary transition-all group flex flex-col justify-between space-y-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center text-green-600 font-bold group-hover:bg-green-600 group-hover:text-white transition-colors">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-black text-sm text-foreground group-hover:text-primary transition-colors">Şeffaf İlan Bilgileri</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">Fiyat, konum ve mülk özelliklerini ilan detaylarından karşılaştırın.</p>
              </div>
            </Link>

            <Link to="/iletisim" className="bg-card p-5 rounded-2xl border border-border/80 shadow-sm hover:shadow-xl hover:border-primary transition-all group flex flex-col justify-between space-y-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 font-bold group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-black text-sm text-foreground group-hover:text-primary transition-colors">Kolay İletişim</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">İlgilendiğiniz mülk için WhatsApp üzerinden görüşme talep edin.</p>
              </div>
            </Link>

            <Link to="/hakkimizda" className="bg-card p-5 rounded-2xl border border-border/80 shadow-sm hover:shadow-xl hover:border-primary transition-all group flex flex-col justify-between space-y-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600 font-bold group-hover:bg-amber-600 group-hover:text-white transition-colors">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-black text-sm text-foreground group-hover:text-primary transition-colors">Yerel Danışmanlık</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">Dürüstlük ve mükemmelliği temel alan şirket modeli.</p>
              </div>
            </Link>

            <Link to="/degerleme" className="bg-card p-5 rounded-2xl border border-border/80 shadow-sm hover:shadow-xl hover:border-primary transition-all group flex flex-col justify-between space-y-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-600 font-bold group-hover:bg-purple-600 group-hover:text-white transition-colors">
                <Calculator className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-black text-sm text-foreground group-hover:text-primary transition-colors">Mülk Değerlendirme</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">Mülkünüzün özellikleriyle değerlendirme görüşmesi talep edin.</p>
              </div>
            </Link>
          </div>
        </section>

        {/* TEKCE Free Expert Advice Container */}
        <section className="py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
          <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6 text-center sm:text-left">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-amber-400/40 shadow-xl shrink-0 hidden sm:block">
                <img src={gurkanImage} alt="Gürkan Sönmez Specialist Agent" className="w-full h-full object-cover" />
              </div>
              <div className="space-y-2">
                <span className="px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 font-black text-xs uppercase tracking-wider">
                  UZMAN EMLAK DANIŞMANLIĞI
                </span>
                <h3 className="text-2xl sm:text-3xl font-black">Satış temsilcimizle iletişime geçin. Uzman görüşü alın!</h3>
                <p className="text-sm text-slate-300 max-w-xl">
                  Corner Gayrimenkul uzmanlarından ücretsiz danışmanlık talep edin. Hayalinizdeki eve en doğru fiyatla sahip olun.
                </p>
              </div>
            </div>

            <form onSubmit={handleLeadSubmit} className="w-full lg:w-auto flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Input
                type="text"
                placeholder="Adınız Soyadınız"
                required
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                className="bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-400 h-12 rounded-xl text-sm w-full sm:w-48"
              />
              <Input
                type="text"
                placeholder="Telefon / E-posta"
                required
                value={leadForm.emailPhone}
                onChange={(e) => setLeadForm({ ...leadForm, emailPhone: e.target.value })}
                className="bg-slate-800/80 border-slate-700 text-white placeholder:text-slate-400 h-12 rounded-xl text-sm w-full sm:w-48"
              />
              <div className="w-full sm:w-auto space-y-2">
                <Button type="submit" size="lg" className="w-full font-black bg-amber-500 hover:bg-amber-600 text-slate-950 h-12 px-6 rounded-xl shadow-lg">
                  <MessageSquare className="h-4 w-4 mr-2" /> DANIŞMAN İLE GÖRÜŞÜN
                </Button>
              </div>
            </form>
          </div>
          <div className="px-2 pt-2 text-right">
            <label className="text-[11px] text-muted-foreground flex items-center justify-end gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={leadForm.agreed}
                onChange={(e) => setLeadForm({ ...leadForm, agreed: e.target.checked })}
                className="rounded border-border"
              />
              <span>Talebim için benimle iletişime geçilmesini kabul ediyorum.</span>
            </label>
          </div>
        </section>

        {/* TEKCE Style Rent / Sale Tabbed Listings Grid */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
          {/* Rent / Sale Header Controls */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-border pb-4 mb-8">
            <div className="flex items-center gap-3 bg-muted p-1.5 rounded-2xl border border-border">
              <button
                onClick={() => setActiveTab("satilik")}
                className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all ${
                  activeTab === "satilik"
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Satılık Gayrimenkuller
              </button>
              <button
                onClick={() => setActiveTab("kiralik")}
                className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all ${
                  activeTab === "kiralik"
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Kiralık Gayrimenkuller
              </button>
            </div>

            <Button asChild variant="outline" className="font-extrabold rounded-xl border-primary text-primary hover:bg-primary hover:text-white">
              <Link to={activeTab === "satilik" ? "/satilik" : "/kiralik"}>
                Tüm Mülkleri Görüntüle <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </Button>
          </div>

          {/* Properties Container Grid */}
          {loading ? <p role="status" className="py-12 text-center text-muted-foreground">Güncel ilanlar yükleniyor…</p> : error ? <p role="alert" className="py-12 text-center text-destructive">İlanlara şu anda ulaşılamıyor. Lütfen yeniden deneyin.</p> : displayedListings.length === 0 ? <div className="rounded-3xl border border-dashed border-border bg-muted/40 p-10 text-center"><Building2 className="h-10 w-10 mx-auto text-primary mb-4" /><h3 className="text-xl font-bold mb-2">Yeni portföyler için görüşelim.</h3><p className="text-muted-foreground mb-5">Şu anda yayınlanmış {activeTab === "satilik" ? "satılık" : "kiralık"} ilan bulunmuyor. Aradığınız mülkün özelliklerini bize iletebilirsiniz.</p><Button asChild><a href="https://wa.me/905335487801" target="_blank" rel="noreferrer"><MessageSquare className="h-4 w-4 mr-2" /> WhatsApp ile danışın</a></Button></div> : null}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedListings.map((property) => (
              <Card key={property.id} className="overflow-hidden group hover:shadow-2xl transition-all duration-300 border border-border/80 bg-card rounded-3xl flex flex-col justify-between">
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={property.images[0]}
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="px-3 py-1 text-[11px] font-black rounded-full bg-slate-950/80 backdrop-blur-md text-amber-300 border border-amber-400/30 uppercase tracking-wider">
                      REF: CRN-{property.id.padStart(4, "0")}
                    </span>
                    {property.featured && (
                      <span className="px-3 py-1 text-[11px] font-black rounded-full bg-primary text-primary-foreground shadow-md">
                        ÖNE ÇIKAN
                      </span>
                    )}
                  </div>
                </div>

                <CardContent className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="text-[11px] font-extrabold text-primary uppercase tracking-wider">
                      TÜRKİYE - ADANA - {(property.ilce || property.mahalle || "SEYHAN").toUpperCase()}
                    </div>

                    <h3 className="font-extrabold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-1">
                      <Link to={`/ilan/${property.slug}`}>
                        {property.title}
                      </Link>
                    </h3>

                    <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                      {property.description}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-border space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold text-muted-foreground">
                      <span>{property.roomCount || PROPERTY_CATEGORY_LABELS[property.category]}</span>
                      <span>{property.grossArea || property.netArea} m²</span>
                      <span>{property.category === "arsa" ? (property.zoning || "Arsa") : property.site ? "Site İçi" : "Müstakil"}</span>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-border/60">
                      <div>
                        <span className="text-[10px] text-muted-foreground block uppercase font-extrabold">FİYAT</span>
                        <span className="text-xl font-black text-primary">
                          {property.price.toLocaleString('tr-TR')} {property.currency || "TL"}
                        </span>
                      </div>

                      <Button asChild size="sm" className="font-bold rounded-xl shadow-xs">
                        <Link to={`/ilan/${property.slug}`}>
                          DETAYLAR
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button
              asChild
              size="lg"
              className="font-black px-10 rounded-2xl shadow-lg bg-slate-950 text-white hover:bg-primary transition-all"
            >
              <Link to={activeTab === "satilik" ? "/satilik" : "/kiralik"}>
                Daha Fazla {activeTab === "satilik" ? "Satılık" : "Kiralık"} Emlak Gör <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </section>

        {/* TEKCE Style News & Market Reports ("Haberler & Analizler") */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/40 border-y border-border">
          <div className="max-w-7xl mx-auto space-y-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 border-b border-border pb-4">
              <div>
                <span className="text-xs font-extrabold text-primary uppercase tracking-wider block mb-1">
                  SEKTÖREL REHBERLER & RAPORLAR
                </span>
                <h2 className="text-3xl font-black text-foreground tracking-tight">Haberler & Analizler</h2>
              </div>
              <Button asChild variant="outline" className="font-bold rounded-xl">
                <Link to="/blog">
                  Tüm Blog Yazılarını Gör (20) <ArrowRight className="h-4 w-4 ml-1.5" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentNews.map((news) => (
                <Card key={news.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300 border border-border/80 bg-card rounded-2xl flex flex-col justify-between">
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img src={news.image} alt={news.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 px-2.5 py-0.5 text-[10px] font-bold rounded-full bg-primary text-primary-foreground shadow-md">
                      {news.category}
                    </span>
                  </div>

                  <CardContent className="p-5 space-y-3 flex-1 flex flex-col justify-between">
                    <div className="space-y-2">
                      <div className="text-[11px] text-muted-foreground font-semibold">{news.publishDate}</div>
                      <h4 className="font-extrabold text-base text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                        <Link to={`/blog/${news.slug}`}>{news.title}</Link>
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                        {news.summary}
                      </p>
                    </div>

                    <Link to={`/blog/${news.slug}`} className="inline-flex items-center text-xs font-bold text-primary hover:underline pt-2 border-t border-border/60">
                      Daha Fazlası <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Lead Capture Banner Sections */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl p-8 sm:p-10 space-y-4 shadow-xl border border-slate-700 flex flex-col justify-between">
              <div>
                <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold uppercase tracking-wider">
                  MÜLK SAHİPLERİNE
                </span>
                <h3 className="text-2xl sm:text-3xl font-black mt-3">Adana'da Evinizi Satmak mı İstiyorsunuz?</h3>
                <p className="text-slate-300 text-sm leading-relaxed mt-2">
                  Gürselpaşa, Seyhan ve Çukurova bölgesindeki gayrimenkulünüzü doğru fiyatlama, profesyonel sunum ve hızlı pazarlama ağı ile değerinde satalım.
                </p>
              </div>
              <Button asChild size="lg" className="w-full font-bold text-base shadow-md rounded-xl">
                <Link to="/gayrimenkulumu-sat">
                  Gayrimenkulünü Sat Formu
                </Link>
              </Button>
            </div>

            <div className="bg-gradient-to-br from-primary via-primary/90 to-primary text-primary-foreground rounded-3xl p-8 sm:p-10 space-y-4 shadow-xl flex flex-col justify-between">
              <div>
                <span className="px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold uppercase tracking-wider">
                  ÜCRETSİZ HİZMET
                </span>
                <h3 className="text-2xl sm:text-3xl font-black mt-3">Evinizin Güncel Piyasa Değerini Öğrenin</h3>
                <p className="text-primary-foreground/90 text-sm leading-relaxed mt-2">
                  Gayrimenkulünüzün bugünkü satış veya kiralama değerini öğrenmek için ücretsiz ekspertiz ve emsal analizi talep edebilirsiniz.
                </p>
              </div>
              <Button asChild size="lg" variant="secondary" className="w-full font-bold text-base shadow-md rounded-xl">
                <Link to="/degerleme">
                  <Calculator className="h-4 w-4 mr-2" /> Ücretsiz Ev Değerleme İste
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <FooterSEO />
      </div>
    </>
  );
};

export default Index;
